import React, { useContext, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Accordion, Icon, Segment, Transition, Form, Tab, TabPane } from 'semantic-ui-react'
import { CoolFarmOptionsContext } from '../../index'
import { useTranslation } from 'react-i18next'
import { StyledAccordion } from '~/pages/Farmer/Faq/style'
import HerdGroupFeeds from './HerdGroupFeeds'
import HerdGroupFeedAdditives from './HerdGroupFeedAdditives'
import InfoPopup from '~/components/InfoPopup'
import { StyledHeader, StyledHerdGroupsWrapper, StyledNoResults } from './style'
import cloneDeep from 'lodash.clonedeep'
import AvgFeeds from './AvgFeeds'
import HerdGroupCrops from './HerdGroupCrops'
import AvgCrops from './AvgCrops'
import { StyledMenuItem } from '~/pages/Farmer/CloudStorage/style'

const Feed: React.FC = () => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()
  const options = useContext(CoolFarmOptionsContext)

  const herdGroups = useWatch({ control, name: 'herdGroups' }) || []
  const watchFeedApproach = useWatch({ control, name: 'feedApproach' })
  const herdCategoryOptions = options?.herdCategories || []
  const hasCrops = (options?.crops || []).length > 0
  const [activeIndex, setActiveIndex] = useState(0)

  const [activeIndexes, setActiveIndexes] = useState<number[]>([])

  const handleAccordionClick = (index: number) => {
    const currentIndex = activeIndexes.indexOf(index)
    const newIndexes = [...activeIndexes]

    if (currentIndex > -1) {
      newIndexes.splice(currentIndex, 1)
    } else {
      newIndexes.push(index)
    }

    setActiveIndexes(newIndexes)
  }

  function getMenuItem(id, label) {
  
    return <StyledMenuItem
      key={id}
      id={id}
      className='tab-item'
      >
      <div>{label}</div>
    </StyledMenuItem>
  }

  function renderDryMatterIntake() {
    return <>
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeIndex}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex as number)}
        panes={herdGroups.map((herdGroup, herdIndex) => {

          const category = herdCategoryOptions.find(cat => cat.id === herdGroup.herdCategoryId)

          if (!category) return null
          return {
            menuItem: getMenuItem(herdGroup.id, category.label),
            pane: <TabPane
              active={herdIndex === activeIndex}
              style={{ border: 'none', marginBottom: 0 }}
              key={herdGroup.id}
            >
              <HerdGroupFeeds
                herdIndex={herdIndex}
                />
              <HerdGroupFeedAdditives
                herdIndex={herdIndex}
                />
              {hasCrops && <HerdGroupCrops
                herdIndex={herdIndex}
                />}
            </TabPane>
          }
        })}
      />
    </>
  }

  function renderAvgFeedUse() {
    return <>
      {herdGroups.map((herdGroup, herdIndex) => {
        const category = herdCategoryOptions.find(cat => cat.id === herdGroup.herdCategoryId)
        const isActive = activeIndexes.includes(herdIndex)

        if (!category) return null

        return (
          <StyledAccordion key={`${herdGroup.herdCategoryId}${herdIndex}`} styled id={herdGroup.id}>
            <Accordion.Title
              active={isActive}
              index={herdIndex}
              onClick={() => handleAccordionClick(herdIndex)}
            >
              <div>{`${category.label}`}</div>
              <Icon name={isActive ? 'minus' : 'plus'} />
            </Accordion.Title>

            <Transition visible={isActive} animation='slide down' duration={250}>
              <Accordion.Content active={isActive} style={{ overflow: 'visible' }}>
                <AvgFeeds herdIndex={herdIndex} />
                {hasCrops && <AvgCrops herdIndex={herdIndex}/>}
              </Accordion.Content>
            </Transition>
          </StyledAccordion>
        )
      })}
    </>
  }

  function handleChangeFeedApproach(feedApproachId) {
    const tempHerdGroups = cloneDeep(herdGroups).filter(herd => herd.herdCategoryId)
    tempHerdGroups.forEach(herdGroup => {
      herdGroup.feeds = []
      herdGroup.feedAdditives = []
    })
    setValue('feedApproach', feedApproachId)
    setValue('herdGroups', tempHerdGroups)
    setActiveIndexes([])
  }

  return (
    <div>
      <div style={{ marginBottom: '2em' }}>
        <StyledHeader>
          {t('Feed Management')}
          <InfoPopup
            content={t('Configure feeds for each herd category')}
          />
        </StyledHeader>
      </div>

      <Segment style={{ padding: '1.5em', marginBottom: '2em' }}>
        <div style={{ marginBottom: '1em' }}>
          <strong style={{ fontSize: '1.1em', color: '#333' }}>
            {t('Select feed data type:')}
          </strong>
        </div>
        <Form.Field>
          <Form.Checkbox
            label={
              <label style={{ fontSize: '1em', fontWeight: 'normal', cursor: 'pointer' }}>
                <strong>{t('Dry matter intake per animal')}</strong>
                <div style={{ color: '#666', fontSize: '0.95em', marginTop: '0.3em' }}>
                  {t('Enter feed data as dry matter intake per individual animal')}
                </div>
              </label>
            }
            name='feedApproach'
            checked={watchFeedApproach === 1}
            onChange={() => handleChangeFeedApproach(1)}
            style={{ marginBottom: '1em' }}
          />
        </Form.Field>
        <Form.Field>
          <Form.Checkbox
            label={
              <label style={{ fontSize: '1em', fontWeight: 'normal', cursor: 'pointer' }}>
                <strong>{t('Average feed use for entire herd')}</strong>
                <div style={{ color: '#666', fontSize: '0.95em', marginTop: '0.3em' }}>
                  {t(`Enter the mix of feed components, percentage by weight, for the entire herd. The percentages should add up to 100%. 
                    Note: the impact of fresh grass, hay and haylage grown on-farm has already been estimated based on the information provided on the Grazing page.`)}
                </div>
              </label>
            }
            name='feedApproach'
            checked={watchFeedApproach === 2}
            onChange={() => handleChangeFeedApproach(2)}
          />
        </Form.Field>
      </Segment>
      <StyledHerdGroupsWrapper>
        {watchFeedApproach === 1 && renderDryMatterIntake()}
        {watchFeedApproach === 2 && renderAvgFeedUse()}
      </StyledHerdGroupsWrapper>
      {!watchFeedApproach && (
        <Segment style={{ padding: '1.5em', marginBottom: '2em' }}>
          <StyledNoResults>
            {t('Please select feed data type')}
          </StyledNoResults>
        </Segment>
      )}
    </div>
  )
}

export default Feed
